"use client"

import { Box, Flex, Image, ImageProps } from "@chakra-ui/react"
import { useEffect, useMemo, useRef, useState } from "react"

import { breakpoints } from "@/theme/breakpoints"

type DirectusTransformOptions = {
  width?: number
  height?: number
  quality?: number
  withoutEnlargement?: boolean
  format?: string
  fit?: string
  focal_point_x?: number
  focal_point_y?: number
}

type BreakpointKey = keyof typeof breakpoints | "base"
type BreakpointWidths = Partial<Record<BreakpointKey, string | number>>

const DEFAULT_TRANSFORM: Partial<DirectusTransformOptions> = {
  fit: "cover",
  format: "webp",
  quality: 70,
}

const EMPTY_BREAKPOINT_WIDTHS: BreakpointWidths = {}

const DEFAULT_LQIP = {
  blurPx: 12,
  enabled: true,
  format: "auto",
  quality: 20,
  width: 20,
}

const DEFAULT_CROP = { enabled: false }

type Props = Omit<
  ImageProps,
  "src" | "srcSet" | "sizes" | "width" | "height"
> & {
  src: string
  alt: string
  noContainer?: boolean
  transform?: Partial<DirectusTransformOptions>
  breakpointWidths?: BreakpointWidths
  originalWidth?: number
  originalHeight?: number
  containerProps?: React.ComponentProps<typeof Flex>
  objectFit?: "cover" | "contain" | "fill" | "none" | "scale-down"
  width?: ImageProps["width"]
  height?: ImageProps["height"]
  lqip?: {
    enabled?: boolean
    width?: number
    quality?: number
    format?: string
    blurPx?: number
  }
  crop?: {
    enabled?: boolean
    aspect?: number
    focalPoint?: { x: number; y: number }
  }
  priority?: boolean
  loadingStrategy?: "lazy" | "eager"
  fetchPriority?: "high" | "low" | "auto"
  sizes?: string
}

function buildDirectusAssetUrl(
  baseUrl: string,
  fileId: string,
  params: DirectusTransformOptions = {}
) {
  const base = (baseUrl || "").replace(/\/+$/, "")
  const assetPath = `/${encodeURIComponent(fileId)}`
  const qs: string[] = []

  if (params.width) qs.push(`width=${Math.round(params.width)}`)
  if (params.height) qs.push(`height=${Math.round(params.height)}`)
  if (typeof params.quality === "number") {
    qs.push(`quality=${Math.round(params.quality)}`)
  }
  if (typeof params.withoutEnlargement === "boolean") {
    qs.push(`withoutEnlargement=${params.withoutEnlargement}`)
  }
  if (params.format) qs.push(`format=${params.format}`)
  if (params.fit) qs.push(`fit=${params.fit}`)
  if (typeof params.focal_point_x === "number") {
    qs.push(`focal_point_x=${params.focal_point_x}`)
  }
  if (typeof params.focal_point_y === "number") {
    qs.push(`focal_point_y=${params.focal_point_y}`)
  }

  return `${base}${assetPath}${qs.length ? `?${qs.join("&")}` : ""}`
}

function normalizePxValue(value: string | number): string {
  if (typeof value === "number") {
    return `${value}px`
  }

  const trimmed = value.trim()
  if (/^\d+$/.test(trimmed)) {
    return `${trimmed}px`
  }

  return trimmed
}

export default function Img({
  alt,
  breakpointWidths = EMPTY_BREAKPOINT_WIDTHS,
  containerProps,
  crop = DEFAULT_CROP,
  fetchPriority,
  height,
  loadingStrategy,
  lqip = DEFAULT_LQIP,
  noContainer = false,
  objectFit,
  originalHeight,
  originalWidth,
  priority = false,
  sizes,
  src,
  transform = DEFAULT_TRANSFORM,
  width,
  ...rest
}: Props) {
  const fileId = src || process.env.DIRECTUS_DEFAULT_THUMBNAIL || ""
  const directusBaseUrl = process.env.DIRECTUS_ASSETS_URL ?? ""
  const desiredAspect =
    crop.aspect ??
    (originalWidth && originalHeight ? originalWidth / originalHeight : 16 / 9)

  const srcWidths = useMemo(() => {
    const values = Object.values(breakpointWidths).reduce<number[]>(
      (widths, value) => {
        const parsedValue =
          typeof value === "string" ? parseInt(value, 10) : value

        if (typeof parsedValue === "number" && !isNaN(parsedValue)) {
          widths.push(Math.round(parsedValue))
        }

        return widths
      },
      []
    )

    if (values.length === 0) {
      return [originalWidth ?? 800]
    }

    const filtered = originalWidth
      ? values.filter((width) => width <= originalWidth)
      : values

    return Array.from(
      new Set((filtered.length > 0 ? filtered : values).sort((a, b) => a - b))
    )
  }, [breakpointWidths, originalWidth])

  const defaultWidth = srcWidths[srcWidths.length - 1] ?? originalWidth ?? 800
  const intrinsicWidth = crop.enabled
    ? defaultWidth
    : (originalWidth ?? defaultWidth)
  const intrinsicHeight = crop.enabled
    ? Math.max(1, Math.round(defaultWidth / desiredAspect))
    : originalHeight

  const srcSet = useMemo(() => {
    return srcWidths
      .map((width) => {
        const height = crop.enabled
          ? Math.max(1, Math.round(width / desiredAspect))
          : undefined

        const url = buildDirectusAssetUrl(directusBaseUrl, fileId, {
          ...(transform as DirectusTransformOptions),
          width,
          ...(height ? { fit: "cover", height } : {}),
          ...(crop.focalPoint && {
            focal_point_x: crop.focalPoint.x,
            focal_point_y: crop.focalPoint.y,
          }),
        })

        return `${url} ${width}w`
      })
      .join(", ")
  }, [
    crop.enabled,
    crop.focalPoint,
    desiredAspect,
    directusBaseUrl,
    fileId,
    srcWidths,
    transform,
  ])

  const srcDirectus = useMemo(() => {
    return buildDirectusAssetUrl(directusBaseUrl, fileId, {
      ...(transform as DirectusTransformOptions),
      width: defaultWidth,
      ...(crop.enabled
        ? {
            fit: "cover",
            height: Math.max(1, Math.round(defaultWidth / desiredAspect)),
          }
        : {}),
      ...(crop.focalPoint && {
        focal_point_x: crop.focalPoint.x,
        focal_point_y: crop.focalPoint.y,
      }),
    })
  }, [
    crop.enabled,
    crop.focalPoint,
    defaultWidth,
    desiredAspect,
    directusBaseUrl,
    fileId,
    transform,
  ])

  const resolvedSizes = useMemo(() => {
    if (sizes) {
      return sizes
    }

    const bpEntries = Object.entries(breakpoints)
    if (bpEntries.length === 0) {
      return undefined
    }

    const parts: string[] = []
    let currentWidth = breakpointWidths.base

    for (const [breakpointKey, breakpointValue] of bpEntries) {
      currentWidth =
        breakpointWidths[breakpointKey as BreakpointKey] ?? currentWidth

      if (!currentWidth) {
        continue
      }

      parts.push(
        `(max-width: ${breakpointValue}) ${normalizePxValue(currentWidth)}`
      )
    }

    parts.push(normalizePxValue(currentWidth ?? "100vw"))

    return parts.join(", ")
  }, [breakpointWidths, sizes])

  const [isLoaded, setIsLoaded] = useState(false)
  const imgRef = useRef<HTMLImageElement | null>(null)

  // If the image is already in the browser cache on mount (e.g. after HMR),
  // onLoad won't fire — check .complete to avoid a permanently blurry image.
  useEffect(() => {
    const revealLoadedImage = () => {
      if (imgRef.current?.complete) {
        setIsLoaded(true)
      }
    }

    revealLoadedImage()

    window.addEventListener("afterprint", revealLoadedImage)
    window.addEventListener("focus", revealLoadedImage)
    window.addEventListener("pageshow", revealLoadedImage)
    document.addEventListener("visibilitychange", revealLoadedImage)

    return () => {
      window.removeEventListener("afterprint", revealLoadedImage)
      window.removeEventListener("focus", revealLoadedImage)
      window.removeEventListener("pageshow", revealLoadedImage)
      document.removeEventListener("visibilitychange", revealLoadedImage)
    }
  }, [])

  useEffect(() => {
    if (isLoaded || !imgRef.current?.complete) {
      return
    }

    if (imgRef.current.naturalWidth > 0) {
      setIsLoaded(true)
    }
  }, [isLoaded, srcDirectus])
  const effectiveLoading = priority ? "eager" : (loadingStrategy ?? "lazy")
  const effectiveFetchPriority = fetchPriority ?? (priority ? "high" : "auto")

  const lqipUrl =
    lqip.enabled !== false
      ? buildDirectusAssetUrl(directusBaseUrl, fileId, {
          format: lqip.format ?? "auto",
          quality: lqip.quality ?? 20,
          width: lqip.width ?? 20,
        })
      : null

  const aspectRatioStyle =
    intrinsicWidth && intrinsicHeight
      ? { aspectRatio: `${intrinsicWidth} / ${intrinsicHeight}` }
      : {}

  const imageProps = {
    alt,
    decoding: "async" as const,
    fetchPriority: effectiveFetchPriority,
    height: noContainer ? height : "100%",
    htmlHeight: intrinsicHeight,
    htmlWidth: intrinsicWidth,
    loading: effectiveLoading,
    objectFit: objectFit ?? transform.fit ?? "cover",
    onLoad: () => setIsLoaded(true),
    opacity: isLoaded ? 1 : 0,
    ref: imgRef,
    sizes: resolvedSizes,
    src: srcDirectus,
    srcSet,
    transition: "opacity 300ms ease",
    width: noContainer ? width : "100%",
    ...rest,
  }

  if (noContainer) {
    return <Image {...imageProps} />
  }

  return (
    <Flex
      alignItems="center"
      justifyContent="center"
      overflow="visible"
      position="relative"
      width={width ?? "100%"}
      {...aspectRatioStyle}
      {...(containerProps ?? {})}
    >
      {lqipUrl && (
        <Box
          backgroundImage={`url("${lqipUrl}")`}
          backgroundPosition="center"
          backgroundSize="cover"
          filter={`blur(${lqip.blurPx ?? 12}px)`}
          inset="0"
          opacity={isLoaded ? 0 : 1}
          position="absolute"
          transform="scale(1.05)"
          transition="opacity 300ms ease"
        />
      )}

      <Image {...imageProps} />
    </Flex>
  )
}
